<template>
    <div class="upload-button-wrapper">
        <FileUpload
            ref="fileUploader"
            mode="advanced"
            name="file"
            :auto="false"
            :accept="buttonConfig.acceptedExtension"
            :disabled="isDisabled"
            :customUpload="true"
            :chooseLabel="buttonConfig.text"
            :chooseIcon="''"
            @select="handleClick"
            aria-labelledby="Upload supplementary files"
            :aria-describedby="`Session has ${props.additionalUploads?.filter((upload: any) => upload.type === 'mp-supplementary')?.length || 0} supplementary files attached`"
        >   
            <template #header="{ chooseCallback }">
                <button
                    type="button"
                    :class="buttonConfig.buttonClass"
                    :disabled="isDisabled"
                    @click="chooseCallback()"
                    :aria-label="buttonConfig.text"
                    @keydown="($event) => {
                        if ($event.key === 'Enter') {
                            $event.preventDefault();
                            chooseCallback();
                        }
                    }"
                >
                    <img
                    :src="props.onInfoPanel ? UploadWhite : UploadIcon"
                    :alt="'Upload icon'"
                    role="presentation"
                    :class="buttonConfig.imageClass"
                    />
                    <span :class="buttonConfig.textClass">{{ buttonConfig.text }}</span>
                </button>
            </template>
        </FileUpload>
    </div>
</template>

<script setup lang="ts">
import UploadIcon from "../assets/images/UploadIcon.svg";
import UploadWhite from "../assets/images/Upload-white.svg";

import type { FileUploadUploaderEvent } from 'primevue/fileupload';

import { computed } from 'vue';
import { useSupplementaryUpload } from '../composables/useSupplementaryUpload';

const { isUploading, handleSupplementaryUpload } = useSupplementaryUpload();

const props = defineProps<{
    type: 'supplementaryFiles' | 'session',
    // uploading supplementary files
    additionalUploads?: Array<{type: string}>,
    sessionId?: string,
    onInfoPanel?: boolean, // custom button styling when it appears in panel
    // uploading sessions
    onSessionClick?: () => void,
}>()

const isSupplementaryLimitReached = computed(() => {
    return (
        props.type === 'supplementaryFiles' &&
        (props.additionalUploads?.filter((upload) => upload.type === 'mp-supplementary')?.length || 0) > 4
    );
});

const isDisabled = computed(() => {
    return isSupplementaryLimitReached.value || isUploading.value;
});

const buttonConfig = computed(() => {
    switch (props.type) {
        case 'supplementaryFiles':
        return {
            text: 'Upload',
            buttonClass: props.onInfoPanel ? 'supplementary-upload-button-info-panel' : 'supplementary-upload-button',
            textClass: props.onInfoPanel ? '' : 'supplementary-button-text',
            imageClass: props.onInfoPanel ? '' : 'supplementary-button-image',
            acceptedExtension: ".pdf",
        }
        case 'session':
        return {
            text: 'Upload file',
            buttonClass: 'session-upload-button',
            textClass: 'session-button-text',
            imageClass: 'session-button-image',
            acceptedExtension: ".m4a,.aac,.caf,.wav,.mp3,.mp4,.webm,.avi",
        }
        default:
        return {
            text: 'Default',
            buttonClass: '',
            textClass: '',
            imageClass: '',
            acceptedExtension: ".m4a,.aac,.caf,.wav,.mp3,.pdf,.mp4,.webm,.avi"
        }
    }
});

const handleClick = async (event: FileUploadUploaderEvent) => {
    if (props.type === 'session'){
        props.onSessionClick?.();
        return;
    }
    await handleSupplementaryUpload(event, props.sessionId);
}

</script>


<style lang="scss" scoped>
@mixin disabled {
    &:disabled {
        color: map-get($colours, grey-4) !important;
        opacity: 60%;
        img {
            opacity: 40%;
        }
    }
}

.supplementary-upload-button {
    @include disabled;
    width: 7.9rem;
    height: 3.2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    padding: 0.8rem 1rem 0.8rem 0.8rem;
    border-radius: 0.8rem;
    background-color: white;

    &:focus-visible {
        border: solid 2px map-get($colours, accent-purple);
        outline: solid 1px rgba(255, 233, 254, 40);
        box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }
}

.supplementary-upload-button-info-panel {
    @include disabled;
    img {
        width: 1.4rem;
        height: 1.4rem;
    }
    height: 2.4rem;
    font-size: 1.2rem;
    // min-width: 7.9rem;
    @include callout;
    color: map-get($colours, grey-1) !important;
    background-color: transparent;
    border: solid 1px #ffffff66;
    border-radius: 0.6rem;
    gap: 0.4rem;
    padding: 0.4rem;
}

.supplementary-button-image {
    width: 1.6rem;
    height: 1.6rem;
    margin: 1.07rem 0 1.07rem 0;
}

.supplementary-button-text {
    width: 4.1rem;
    height: 1.6rem;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.33;
    text-align: center;
    color: black;
    font-family: $font-secondary;
}

.session-upload-button{
    width: 13.9rem;
    height: 4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    border-radius: 1rem;
    border: solid 0.15rem;
    overflow: hidden;
    background-color: transparent;

    &:focus-visible {
        border: solid 2px map-get($colours, accent-purple);
        outline: solid 1px rgba(255, 233, 254, 40);
        box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }
}

.session-button-image {
    width: 2.4rem;
    height: 2.4rem;
    filter: brightness(0) invert(1); 
}

.session-button-text {
    width: 8.3rem;
    height: 2.4rem;
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1.5;
    text-align: center;
    color: white;
    font-family: $font-secondary;
}

.upload-button-wrapper {
  margin: 0;
  padding: 0;

    &:focus-visible {
        border: solid 2px map-get($colours, accent-purple);
        outline: solid 1px rgba(255, 233, 254, 40);
        box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }   
  ::v-deep(.p-fileupload-advanced) {
    margin: 0;
    padding: 0;
    background: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background: grey;

    .p-fileupload-header {
      padding: 0;
      border: none;
    }

    .p-fileupload-content {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      border: none;
    }
  }

  ::v-deep(.p-fileupload-content) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    padding: 0 !important;
    overflow: hidden;
    border: none;
  }

  button {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

</style>